import { GridColDef } from '@mui/x-data-grid-pro';
import { ReactElement } from 'react';
import { getConstant } from '../../utility/constantsTagsUtils';
import { Constants } from '../graph/StateTags';
import { Label, Schema, TextFields } from "@mui/icons-material";

export interface CommonColumnData
{
    order: number
    width: number
    hidden?: boolean
    textWrap?: boolean
}

export type ColumnSourceType = "constant" | "state" | "custom" | "auto"
export type ValueOptions = { [id: string]: string };
export interface CustomColumnData extends CommonColumnData
{
    columnType: "custom",
    name: string,
    type: string,
    valueOptions?: ValueOptions
}

export interface ConstantColumnData extends CommonColumnData
{
    columnType: "constant"
    source: string,
}

export interface StateColumnData extends CommonColumnData
{
    columnType: "state"
    source: string,
}

export interface AutoColumnData extends CommonColumnData
{
    columnType: "auto"
    source: string,
}

export type ColumnData = ConstantColumnData | CustomColumnData | StateColumnData | AutoColumnData

export interface CustomGridColDef extends GridColDef
{
    id: string
    hidden: boolean
    sourceType: ColumnSourceType
    textWrap: boolean
    order: number
    onSetHeight: (id: string, field: string, height: number) => void
}

interface AutoColDef
{
    name: string
    type: string
}

export const autoColDefs: { [id: string]: AutoColDef, default: AutoColDef } = {
    _group: {
        name: "Group By",
        type: "string",
    },
    default: {
        name: "",
        type: "string",
    }
}

export const stateDataTypes = {
    _stateId: {
        name: "ULID",
        type: "string"
    },
    _title: {
        name: "Title",
        type: "string"
    },
    _script: {
        name: "Node Text",
        type: "string"
    },
    _transitionCount: {
        name: "Exit Count",
        type: "number"
    },
    _transitionName: {
        name: "Exit Name",
        type: "string"
    },
    _transitionDestinations: {
        name: "Exit Destinations",
        type: "string"
    },
    _blockNames: {
        name: "Block Names",
        type: "string"
    },
    _blockChapters: {
        name: "Block Chapters",
        type: "string"
    },
    _blockIDs: {
        name: "Block IDs",
        type: "string"
    },
    _blockNumbers: {
        name: "Block Numbers",
        type: "string"
    }
}

export type StateDataTypeKeys = keyof typeof stateDataTypes;

export interface ColumnDetails
{
    name: string,
    type: string,
    field: string,
    icon?: () => ReactElement
    valueOptions?: ValueOptions
}

export function getColumnDetails(columnEntry: [string, ColumnData] | undefined, constants: Constants): ColumnDetails | null
{
    if (columnEntry)
    {
        const [id, column] = columnEntry;
        const missing = { name: "Missing", field: "", type: "string"};

        switch (column?.columnType)
        {
            case "auto":
                const autoCol = autoColDefs[column.source] ?? autoColDefs.default;
                return { name: autoCol.name, field: id, type: autoCol.type };
            case "custom":
                return { name: column.name, field: id, type: column.type, valueOptions: column.valueOptions, icon: () => <TextFields fontSize={"small"} /> };
            case "state":
                const stateCol = stateDataTypes[column.source as StateDataTypeKeys];
                
                return stateCol ? {
                    name: stateCol.name,
                    field: column.source,
                    type: stateCol.type,
                    icon: () => <Schema fontSize={"small"} sx={{height: "0.8em"}}/>
                } : missing;
            case "constant":
                const constantCol = getConstant(column.source, constants);
                return {
                    name: constantCol?.name || column.source,
                    field: column.source,
                    type: constantCol?.type || "string",
                    icon: () => <Label fontSize={"small"} sx={{height: "0.8em"}}/>
                };
        }
    }
    return null;
}